import React, { useState, useEffect, useCallback } from 'react'
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Download,
  Wifi,
  WifiOff,
  Clock,
  HardDrive,
} from 'lucide-react'
import { Modal } from '../shared/Modal'
import { offlineIndexedDb } from '../../lib/offline/indexedDb'
import { syncManager, SyncEventPayload } from '../../lib/offline/syncManager'
import { OutboxOperation, ConflictRecord } from '../../types/offline'

interface SyncOperationsModalProps {
  isOpen: boolean
  onClose: () => void
  captureId: string
  teamId: string
  userId: string
  onSyncCompleted?: () => void
}

export const SyncOperationsModal: React.FC<SyncOperationsModalProps> = ({
  isOpen,
  onClose,
  captureId,
  teamId,
  userId,
  onSyncCompleted,
}) => {
  const [pendingOps, setPendingOps] = useState<OutboxOperation[]>([])
  const [isSyncing, setIsSyncing] = useState(false)
  const [isReachable, setIsReachable] = useState(true)
  const [activeConflict, setActiveConflict] = useState<ConflictRecord | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [exportSuccess, setExportSuccess] = useState(false)

  const loadData = useCallback(async () => {
    try {
      const ops = await offlineIndexedDb.getPendingOutboxOperations(teamId, userId)
      const captureOps = ops.filter(
        (o) => o.capture_id === captureId || o.entity_id === captureId || !o.capture_id
      )
      setPendingOps(captureOps)

      const reachable = await syncManager.checkReachability()
      setIsReachable(reachable)

      const conflictOp = captureOps.find((o) => o.status === 'conflict')
      if (conflictOp) {
        setActiveConflict({
          operation_id: conflictOp.operation_id,
          entity_id: conflictOp.entity_id,
          local_payload: conflictOp.payload,
          message: conflictOp.last_error || 'Conflicto de edición concurrente detectado.',
          created_at: conflictOp.client_created_at,
        })
      } else {
        setActiveConflict(null)
      }
    } catch (err) {
      console.warn('Error al cargar cola outbox:', err)
    }
  }, [captureId, teamId, userId])

  useEffect(() => {
    if (!isOpen) return
    loadData()

    const unsubscribe = syncManager.subscribe((event: SyncEventPayload) => {
      if (event.type === 'sync_started') {
        setIsSyncing(true)
        setStatusMessage('Sincronizando operaciones con el servidor...')
      } else if (event.type === 'sync_progress') {
        setStatusMessage(`Sincronizando... Quedan ${event.pendingCount} operaciones`)
      } else if (event.type === 'sync_completed') {
        setIsSyncing(false)
        setStatusMessage(
          event.pendingCount === 0
            ? 'Todas las jugadas están sincronizadas.'
            : `Quedan ${event.pendingCount} operaciones pendientes.`
        )
        loadData()
      } else if (event.type === 'sync_error') {
        setIsSyncing(false)
        setStatusMessage(`Error de sincronización: ${event.error || 'Fallo de conexión'}`)
        loadData()
      } else if (event.type === 'conflict_detected') {
        setIsSyncing(false)
        if (event.conflict) setActiveConflict(event.conflict)
        loadData()
      }
    })

    return () => unsubscribe()
  }, [isOpen, loadData])

  const handleSyncNow = async () => {
    setIsSyncing(true)
    setStatusMessage('Comprobando conexión y enviando operaciones...')
    try {
      const res = await syncManager.triggerSync('modal_sync_now', teamId, userId)
      if (res.conflictsCount > 0) {
        setStatusMessage('Atención: Se detectó un conflicto de versión en una de las jugadas.')
      } else if (res.failedCount > 0) {
        setStatusMessage('Algunas operaciones no se pudieron enviar. Se reintentarán automáticamente.')
      } else {
        setStatusMessage('Sincronización completada con éxito.')
      }
      await loadData()
      if (onSyncCompleted) onSyncCompleted()
    } catch (err: unknown) {
      setStatusMessage(err instanceof Error ? err.message : 'Error al sincronizar')
    } finally {
      setIsSyncing(false)
    }
  }

  const handleExportEmergency = async () => {
    try {
      const jsonStr = await offlineIndexedDb.exportEmergencyBackup(teamId, userId)
      const blob = new Blob([jsonStr], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `team_spain_backup_${teamId.slice(0, 8)}_${new Date().toISOString().replace(/[:.]/g, '-')}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setExportSuccess(true)
      setTimeout(() => setExportSuccess(false), 4000)
    } catch (err) {
      console.error('Error al exportar copia de seguridad:', err)
    }
  }

  const formatOpType = (type: string) => {
    switch (type) {
      case 'commit_snap':
        return 'Guardar Snap'
      case 'correct_snap_result':
        return 'Corregir Resultado'
      case 'update_snap_details':
        return 'Modificar Snap'
      case 'void_snap':
        return 'Anular Snap'
      case 'finalize_capture':
        return 'Finalizar Bloque'
      case 'reopen_capture':
        return 'Reabrir Sesión'
      case 'update_match_context':
        return 'Actualizar Marcador'
      case 'update_active_lineups':
        return 'Actualizar Quintetos'
      default:
        return type
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Estado de Sincronización y Guardado Local"
      size="lg"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleExportEmergency}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            title="Descarga un archivo JSON de respaldo de las operaciones locales pendientes"
          >
            <Download size={15} />
            <span>Exportar copia deportiva</span>
          </button>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSyncNow}
              disabled={isSyncing}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: '140px' }}
            >
              <RefreshCw size={15} className={isSyncing ? 'spin' : ''} />
              <span>{isSyncing ? 'Sincronizando...' : 'Sincronizar ahora'}</span>
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Banner de Estado de Conexión */}
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: isReachable ? 'var(--status-success-bg)' : 'var(--brand-yellow-subtle)',
            border: `1px solid ${isReachable ? 'var(--status-success-border)' : 'var(--brand-yellow)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {isReachable ? (
              <Wifi size={20} color="var(--status-success-text)" />
            ) : (
              <WifiOff size={20} color="var(--brand-yellow)" />
            )}
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: isReachable ? 'var(--status-success-text)' : 'var(--text-main)' }}>
                {isReachable ? 'Conexión con el Servidor Activa' : 'Sin Conexión al Servidor (Modo Campo Offline)'}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {isReachable
                  ? 'Las operaciones se envían automáticamente al registrarse.'
                  : 'Tus jugadas se guardan de forma duradera en este dispositivo en IndexedDB.'}
              </div>
            </div>
          </div>
          <span className={`badge ${isReachable ? 'badge-success' : 'badge-yellow'}`}>
            {isReachable ? 'En Línea' : 'Offline'}
          </span>
        </div>

        {/* Mensaje de estado dinámico */}
        {statusMessage && (
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
            {statusMessage}
          </div>
        )}

        {/* Alerta de exportación exitosa */}
        {exportSuccess && (
          <div className="alert alert-success" style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
            <CheckCircle size={16} />
            <span>Copia de seguridad descargada correctamente en tu dispositivo.</span>
          </div>
        )}

        {/* Vista de Conflicto si existe */}
        {activeConflict && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--brand-red-subtle)',
              border: '1px solid var(--brand-red)',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--brand-red)', fontWeight: 600 }}>
              <AlertTriangle size={18} />
              <span>Conflicto de Edición Concurrente Detectado</span>
            </div>
            <p style={{ fontSize: '0.85rem', margin: 0, color: 'var(--text-main)' }}>
              {activeConflict.message}
            </p>
            <p style={{ fontSize: '0.8rem', margin: 0, color: 'var(--text-muted)' }}>
              Otro dispositivo ha modificado datos de esta captura. Tu versión local se conserva intacta
              y puedes exportarla o forzar reintento con la versión más reciente del servidor.
            </p>
          </div>
        )}

        {/* Lista de operaciones en Outbox */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h4 style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <HardDrive size={16} />
              <span>Cola de Operaciones Locales ({pendingOps.length})</span>
            </h4>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Ordenadas por secuencia de ejecución
            </span>
          </div>

          {pendingOps.length === 0 ? (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                backgroundColor: 'var(--surface-subtle)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-muted)',
                fontSize: '0.85rem',
              }}
            >
              <CheckCircle size={28} color="var(--status-success-text)" style={{ margin: '0 auto 8px', display: 'block' }} />
              No hay operaciones pendientes de sincronización. Todas las jugadas están confirmadas en el servidor.
            </div>
          ) : (
            <div
              style={{
                maxHeight: '260px',
                overflowY: 'auto',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {pendingOps.map((op, idx) => (
                <div
                  key={op.operation_id}
                  style={{
                    padding: '10px 12px',
                    borderBottom: idx < pendingOps.length - 1 ? '1px solid var(--border-color)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.85rem',
                    backgroundColor: op.status === 'conflict' ? 'var(--brand-red-subtle)' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--surface-subtle)',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      {idx + 1}
                    </span>
                    <div>
                      <div style={{ fontWeight: 600 }}>
                        {formatOpType(op.operation_type)}
                        {op.payload?.snap?.sequence !== undefined && ` (Snap #${op.payload.snap.sequence})`}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={11} />
                        <span>{new Date(op.client_created_at).toLocaleTimeString('es-ES')}</span>
                        {op.attempts > 0 && <span>· {op.attempts} reintentos</span>}
                        {op.last_error && <span style={{ color: 'var(--brand-red)' }}>· {op.last_error}</span>}
                      </div>
                    </div>
                  </div>

                  <div>
                    {op.status === 'pending' && <span className="badge badge-yellow">Guardado local</span>}
                    {op.status === 'syncing' && <span className="badge badge-blue">Sincronizando...</span>}
                    {op.status === 'conflict' && <span className="badge badge-red">Conflicto</span>}
                    {op.status === 'failed' && <span className="badge badge-red">Error</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Nota explicativa de funcionamiento en campo */}
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            lineHeight: 1.4,
            padding: '8px 12px',
            backgroundColor: 'var(--surface-subtle)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <strong>Garantía de persistencia offline:</strong> Los datos se guardan de forma atómica en la base de datos
          IndexedDB de este navegador antes de avanzar de jugada. Si recargas o cierras el navegador,
          el trabajo no se pierde y continuará la sincronización al reabrir la consola.
        </div>
      </div>
    </Modal>
  )
}
